import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import useSwipe from "../../hooks/useSwipe";
import useFocusTrap from "../../hooks/useFocusTrap";
import { useFavorites } from "../../context/FavoritesContext";
import { useShare } from "../../hooks/useShare";
import { formatPrice } from "../../utils/formatPrice";
import { getOptimizedImageUrl, getImageSrcSet } from "../../utils/imageUrl";
import StoryProgress from "./StoryProgress";
import "./StoryViewer.css";

function StoryViewer({
  isOpen,
  stories,
  initialStoryIndex = 0,
  getItemsForStory,
  onClose,
  onAddToCart,
  getTranslatedText,
  onStoryViewed,
  restaurantId,
  tableNumber,
  mode = "dine_in",
}) {
  const { t } = useTranslation();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { shareProduct } = useShare();
  const dialogRef = useFocusTrap(isOpen);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(initialStoryIndex);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [heartAnimating, setHeartAnimating] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);

  // Reset state when opening with new story
  useEffect(() => {
    if (isOpen) {
      setCurrentStoryIndex(initialStoryIndex);
      setCurrentItemIndex(0);
      setIsPaused(false);
    }
  }, [isOpen, initialStoryIndex]);

  // Get current story and items
  const currentStory = stories?.[currentStoryIndex];
  const items = currentStory ? getItemsForStory(currentStory.id) : [];
  const currentItem = items[currentItemIndex];

  // Mark story as viewed when entering
  useEffect(() => {
    if (isOpen && currentStory) {
      onStoryViewed?.(currentStory.id);
    }
  }, [isOpen, currentStory, onStoryViewed]);

  // Navigation functions
  const goToNextItem = useCallback(() => {
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex((prev) => prev + 1);
    } else if (currentStoryIndex < stories.length - 1) {
      // Next story
      setCurrentStoryIndex((prev) => prev + 1);
      setCurrentItemIndex(0);
    } else {
      // End of all stories
      onClose();
    }
  }, [
    currentItemIndex,
    currentStoryIndex,
    items.length,
    stories.length,
    onClose,
  ]);

  const goToPrevItem = useCallback(() => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex((prev) => prev - 1);
    } else if (currentStoryIndex > 0) {
      // Previous story - go to last item
      const prevStoryItems = getItemsForStory(
        stories[currentStoryIndex - 1].id,
      );
      setCurrentStoryIndex((prev) => prev - 1);
      setCurrentItemIndex(prevStoryItems.length - 1);
    }
  }, [currentItemIndex, currentStoryIndex, stories, getItemsForStory]);

  // Swipe handlers
  const swipeHandlers = useSwipe({
    onSwipeLeft: goToNextItem,
    onSwipeRight: goToPrevItem,
    onSwipeDown: onClose,
    threshold: 50,
  });

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowRight":
          goToNextItem();
          break;
        case "ArrowLeft":
          goToPrevItem();
          break;
        case "Escape":
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, goToNextItem, goToPrevItem, onClose]);

  // Handle tap navigation (left/right areas)
  const handleTapNavigation = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;

    // Left 30% = previous, Right 70% = next
    if (x < width * 0.3) {
      goToPrevItem();
    } else {
      goToNextItem();
    }
  };

  // Handle add to cart
  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (currentItem) {
      onAddToCart(currentItem);
    }
  };

  // Handle toggle favorite
  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    if (currentItem) {
      toggleFavorite(currentItem.itemId);
      // Animate heart
      if (!isFavorite(currentItem.itemId)) {
        setHeartAnimating(true);
        setTimeout(() => setHeartAnimating(false), 400);
      }
    }
  };

  // Handle share
  const handleShare = async (e) => {
    e.stopPropagation();
    if (currentItem) {
      const result = await shareProduct(
        currentItem,
        restaurantId,
        tableNumber,
        mode,
      );
      if (result.success && result.method === "clipboard") {
        setShowShareToast(true);
        setTimeout(() => setShowShareToast(false), 2000);
      }
    }
  };

  const isCurrentItemFavorite = currentItem
    ? isFavorite(currentItem.itemId)
    : false;

  if (!isOpen || !currentStory || !currentItem) {
    return null;
  }

  const name = getTranslatedText(currentItem, "name");
  const description = getTranslatedText(currentItem, "description");
  const price = currentItem.price;
  const originalPrice = currentItem.originalPrice;
  const hasDiscount = originalPrice && originalPrice > price;

  return (
    <div
      className="story-viewer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="story-viewer-title"
      ref={dialogRef}
    >
      <div className="story-viewer__backdrop" onClick={onClose} />

      <div
        className="story-viewer__content"
        {...swipeHandlers}
        onClick={handleTapNavigation}
      >
        {/* Header */}
        <div className="story-viewer__header">
          <StoryProgress
            total={items.length}
            current={currentItemIndex}
            isPaused={isPaused}
          />

          <div className="story-viewer__header-info">
            <button
              className="story-viewer__close"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label={t('stories.close')}
            >
              <span className="material-icons" aria-hidden="true">close</span>
            </button>

            <div className="story-viewer__story-info">
              <div className="story-viewer__story-avatar">
                {currentStory.imageUrl ? (
                  <img
                    src={getOptimizedImageUrl(currentStory.imageUrl, {
                      width: 32,
                    })}
                    srcSet={getImageSrcSet(currentStory.imageUrl, 32)}
                    sizes="32px"
                    alt={currentStory.name}
                    width={32}
                    height={32}
                    decoding="async"
                  />
                ) : (
                  <span className="material-icons" aria-hidden="true">restaurant_menu</span>
                )}
              </div>
              <span className="story-viewer__story-name">
                {currentStory.name}
              </span>
            </div>

            <span className="story-viewer__counter">
              {currentItemIndex + 1} {t("stories.of", "de")} {items.length}
            </span>
          </div>
        </div>

        {/* Image */}
        <div className="story-viewer__image-container">
          {currentItem.imageUrl ? (
            <img
              src={getOptimizedImageUrl(currentItem.imageUrl, { width: 480 })}
              srcSet={getImageSrcSet(currentItem.imageUrl, 480)}
              sizes="100vw"
              alt={name}
              decoding="async"
              className="story-viewer__image"
            />
          ) : (
            <div className="story-viewer__image-placeholder">
              <span className="material-icons" aria-hidden="true">restaurant</span>
            </div>
          )}
        </div>

        {/* Product Info Footer */}
        <div
          className="story-viewer__footer"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="story-viewer__product-info">
            <h3 className="story-viewer__product-name" id="story-viewer-title">{name}</h3>
            {description && (
              <p className="story-viewer__product-description">{description}</p>
            )}
            <div className="story-viewer__product-price">
              {hasDiscount && (
                <span className="story-viewer__original-price">
                  ${formatPrice(originalPrice)}
                </span>
              )}
              <span className="story-viewer__price">${formatPrice(price)}</span>
            </div>
          </div>

          <div className="story-viewer__actions">
            <button
              className={`story-viewer__favorite-btn ${isCurrentItemFavorite ? "active" : ""} ${heartAnimating ? "animating" : ""}`}
              onClick={handleToggleFavorite}
              aria-label={
                isCurrentItemFavorite
                  ? t("favorites.remove")
                  : t("favorites.add")
              }
            >
              <span className="material-icons" aria-hidden="true">
                {isCurrentItemFavorite ? "favorite" : "favorite_border"}
              </span>
            </button>

            <button
              className="story-viewer__share-btn"
              onClick={handleShare}
              aria-label={t("share.share")}
            >
              <span className="material-icons" aria-hidden="true">share</span>
            </button>

            <button className="story-viewer__add-btn" onClick={handleAddToCart}>
              <span className="material-icons" aria-hidden="true">add</span>
              {t("stories.addToCart", "Agregar")}
            </button>
          </div>

          {/* Share Toast */}
          {showShareToast && (
            <div className="story-viewer__toast">{t("share.linkCopied")}</div>
          )}
        </div>

        {/* Navigation hints (visual only) */}
        <div className="story-viewer__nav-hint story-viewer__nav-hint--left">
          <span className="material-icons" aria-hidden="true">chevron_left</span>
        </div>
        <div className="story-viewer__nav-hint story-viewer__nav-hint--right">
          <span className="material-icons" aria-hidden="true">chevron_right</span>
        </div>
      </div>
    </div>
  );
}

export default StoryViewer;
